import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Circle, Play, Book, FileText, Download, Award, Users, Clock, Star, Globe, Shield, Zap, GraduationCap, Sparkles } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { AuthModal } from '@/components/AuthModal';
import { ProfileSetup } from '@/components/ProfileSetup';
import { LessonViewer } from '@/components/LessonViewer';
import { BadgeViewer } from '@/components/BadgeViewer';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useSEO } from '@/hooks/useSEO';

// Academy page component

export const Academy = () => {
  const { t } = useLanguage();
  const { user, loading } = useAuth();
  const [completedLessons, setCompletedLessons] = useState<number[]>([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [courseData, setCourseData] = useState<any>(null);
  const [userEnrollment, setUserEnrollment] = useState<any>(null);
  const [currentLesson, setCurrentLesson] = useState<any>(null);
  const [lessonProgress, setLessonProgress] = useState<Record<number, any>>({});

  // SEO optimization
  useSEO({
    title: 'Free AI Fundamentals & Prompt Engineering Course | QuantumBox Academy',
    description: 'Master AI fundamentals and prompt engineering with our free 15-hour certified course. Learn from industry experts, get hands-on experience, and earn a professional certificate from QuantumBox AI.',
    keywords: 'free ai course, ai fundamentals, prompt engineering, ai certification, machine learning basics, artificial intelligence training, quantumbox academy',
    ogTitle: 'Free AI Fundamentals & Prompt Engineering Course - QuantumBox Academy',
    ogDescription: 'Join thousands of students learning AI fundamentals and prompt engineering. Free certification, 15 hours of content, and industry-recognized credentials.'
  });

  useEffect(() => {
    loadCourseData();
    if (user) {
      checkUserEnrollment();
      loadUserProgress();
    }
  }, [user]);

  const loadUserProgress = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('lesson_progress')
        .select('lesson_id, completed, completed_at, time_spent_minutes')
        .eq('user_id', user.id);

      if (error) throw error;

      console.log('User progress data:', data);

      if (data) {
        const progressMap = data.reduce((acc, progress) => {
          acc[progress.lesson_id] = progress;
          return acc;
        }, {});
        setLessonProgress(progressMap);
        
        // Update completed lessons array
        const completed = data.filter(p => p.completed).map(p => parseInt(p.lesson_id));
        setCompletedLessons(completed);
        
        console.log('Completed lessons:', completed);
        console.log('Final exam completed:', progressMap['21']?.completed);
      }
    } catch (error) {
      console.error('Error loading user progress:', error);
    }
  };

  const loadCourseData = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('title', 'AI Fundamentals & Prompt Engineering')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setCourseData(data);
      } else {
        // Use fallback course data if not found in database
        console.log('Using fallback course data');
      }
    } catch (error) {
      console.error('Error loading course:', error);
    }
  };

  const checkUserEnrollment = async () => {
    if (!user) return;
    
    // Use courseData.id if available, otherwise use fallback course ID
    const courseId = courseData?.id || '741aed38-dd83-4031-b5d8-418fa0e5bb4c';

    try {
      const { data, error } = await supabase
        .from('user_enrollments')
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setUserEnrollment(data);
      }
    } catch (error) {
      console.error('Error checking enrollment:', error);
    }
  };

  const handleEnrollment = async () => {
    if (!user || !courseData) return;

    // Check if already enrolled first
    if (userEnrollment) {
      toast.success('You are already enrolled in this course!');
      return;
    }

    try {
      const { error } = await supabase
        .from('user_enrollments')
        .insert({
          user_id: user.id,
          course_id: courseData.id,
        });

      if (error) {
        if (error.code === '23505') {
          // Duplicate key error - user already enrolled
          toast.success('You are already enrolled in this course!');
          checkUserEnrollment();
          return;
        }
        throw error;
      }
      
      toast.success('Successfully enrolled in the course!');
      checkUserEnrollment();
    } catch (error) {
      toast.error('Failed to enroll in course');
      console.error('Enrollment error:', error);
    }
  };

  const course = courseData || {
    title: "AI Fundamentals & Prompt Engineering",
    description: "Master the fundamentals of Artificial Intelligence and learn advanced prompt engineering techniques. This comprehensive course covers AI basics, machine learning concepts, and practical prompt engineering strategies used by professionals.",
    duration_hours: 15,
    difficulty_level: "Beginner",
    price: 0,
    is_free: true,
    instructor_name: "QuantumBox AI Team"
  };

  const modules = [
    {
      id: 1,
      title: "Introduction to AI",
      description: "Foundation concepts and historical context of artificial intelligence",
      lessons: [
        { id: 1, title: "What is Artificial Intelligence?", duration: "45 min", type: "reading", description: "Comprehensive overview of AI definitions and applications" },
        { id: 2, title: "History and Evolution of AI", duration: "30 min", type: "reading", description: "From Turing machines to modern AI systems" },
        { id: 3, title: "AI vs Machine Learning vs Deep Learning", duration: "50 min", type: "reading", description: "Understanding the relationship between these fields" },
        { id: 4, title: "Quiz: AI Fundamentals", duration: "15 min", type: "quiz", description: "Test your understanding of basic AI concepts" }
      ]
    },
    {
      id: 2,
      title: "Machine Learning Essentials",
      description: "Core machine learning concepts and methodologies",
      lessons: [
        { id: 5, title: "Types of Machine Learning", duration: "40 min", type: "reading", description: "Supervised, unsupervised, and reinforcement learning" },
        { id: 6, title: "Supervised Learning Deep Dive", duration: "55 min", type: "reading", description: "Classification and regression techniques" },
        { id: 7, title: "Unsupervised Learning Applications", duration: "45 min", type: "reading", description: "Clustering, dimensionality reduction, and pattern discovery" },
        { id: 8, title: "Quiz: ML Fundamentals", duration: "20 min", type: "quiz", description: "Validate your machine learning knowledge" }
      ]
    },
    {
      id: 3,
      title: "Neural Networks & Deep Learning",
      description: "Understanding neural networks and deep learning architectures",
      lessons: [
        { id: 9, title: "Introduction to Neural Networks", duration: "50 min", type: "reading", description: "Perceptrons, multilayer networks, and backpropagation" },
        { id: 10, title: "Deep Learning Architectures", duration: "60 min", type: "reading", description: "CNNs, RNNs, and Transformers explained" },
        { id: 11, title: "Training Deep Neural Networks", duration: "40 min", type: "reading", description: "Optimization techniques and best practices" },
        { id: 12, title: "Quiz: Deep Learning", duration: "25 min", type: "quiz", description: "Test your neural networks understanding" }
      ]
    },
    {
      id: 4,
      title: "Prompt Engineering Mastery",
      description: "Advanced techniques for effective AI communication",
      lessons: [
        { id: 13, title: "Foundations of Prompt Engineering", duration: "45 min", type: "reading", description: "Basic principles and prompt structure" },
        { id: 14, title: "Advanced Prompt Techniques", duration: "75 min", type: "reading", description: "Chain-of-thought, few-shot learning, and complex reasoning" },
        { id: 15, title: "Industry Applications & Case Studies", duration: "50 min", type: "reading", description: "Real-world prompt engineering examples" },
        { id: 16, title: "Hands-on Prompt Playground (Live AI)", duration: "30 min", type: "playground", description: "Try 3 prompt techniques live against a real LLM — required to complete" },
        { id: 17, title: "Quiz: Prompt Engineering", duration: "30 min", type: "quiz", description: "Master prompt engineering techniques" }
      ]
    },
    {
      id: 5,
      title: "AI Ethics & Future Trends",
      description: "Responsible AI development and emerging technologies",
      lessons: [
        { id: 18, title: "AI Ethics and Bias", duration: "40 min", type: "reading", description: "Understanding fairness, accountability, and transparency" },
        { id: 19, title: "AI Safety and Alignment", duration: "35 min", type: "reading", description: "Ensuring AI systems behave as intended" },
        { id: 20, title: "Future of AI Technology", duration: "45 min", type: "reading", description: "Emerging trends and breakthrough technologies" },
        { id: 21, title: "Final Comprehensive Assessment", duration: "45 min", type: "final_exam", description: "Complete course evaluation and certification quiz" }
      ]
    }
  ];

  const totalLessons = modules.reduce((acc, module) => acc + module.lessons.length, 0);
  const completionPercentage = Math.round((completedLessons.length / totalLessons) * 100);

  const toggleLessonCompletion = (lessonId: number) => {
    setCompletedLessons(prev => 
      prev.includes(lessonId) 
        ? prev.filter(id => id !== lessonId)
        : [...prev, lessonId]
    );
  };

  const getLessonIcon = (type: string) => {
    switch (type) {
      case 'reading': return <Book className="h-4 w-4" />;
      case 'quiz': return <FileText className="h-4 w-4" />;
      case 'playground': return <Sparkles className="h-4 w-4" />;
      case 'final_exam': return <Award className="h-4 w-4" />;
      default: return <Book className="h-4 w-4" />;
    }
  };

  const getBadgeColor = (type: string) => {
    switch (type) {
      case 'reading': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'quiz': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'playground': return 'bg-primary/15 text-primary';
      case 'final_exam': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const handleStartCourse = () => {
    if (!user) {
      setShowAuthModal(true);
    } else if (userEnrollment) {
      // Start with first lesson
      const firstLesson = modules[0].lessons[0];
      setCurrentLesson(firstLesson);
      toast.success('Welcome back to your course!');
    } else {
      handleEnrollment();
    }
  };

  const isLessonUnlocked = (lessonId: number) => {
    // First lesson is always unlocked
    if (lessonId === 1) return true;
    
    // Check if all previous lessons are completed
    const allLessons = modules.flatMap(module => module.lessons);
    const lessonIndex = allLessons.findIndex(lesson => lesson.id === lessonId);
    
    if (lessonIndex === -1) return false;
    
    // Check if all previous lessons are completed
    for (let i = 0; i < lessonIndex; i++) {
      const previousLessonId = allLessons[i].id;
      if (!completedLessons.includes(previousLessonId)) {
        return false;
      }
    }
    
    return true;
  };

  const handleLessonClick = (lesson: any) => {
    if (!user || !userEnrollment) {
      toast.error('Please enroll in the course first');
      return;
    }

    if (!isLessonUnlocked(lesson.id)) {
      toast.error('Please complete the previous lessons first');
      return;
    }
    
    setCurrentLesson(lesson);
  };

  const handleLessonComplete = async (score?: number) => {
    if (currentLesson && user) {
      await updateLessonProgress(currentLesson.id, true, score);
      setCurrentLesson(null);
    }
  };

  const updateLessonProgress = async (lessonId: number, completed: boolean, score?: number) => {
    if (!user) return;

    try {
      const { data: existing, error: fetchError } = await supabase
        .from('lesson_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('lesson_id', lessonId.toString())
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      const progressData = {
        user_id: user.id,
        lesson_id: lessonId.toString(),
        completed,
        completed_at: completed ? new Date().toISOString() : null,
        time_spent_minutes: existing?.time_spent_minutes || 0
      };

      if (existing) {
        const { error } = await supabase
          .from('lesson_progress')
          .update(progressData)
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('lesson_progress')
          .insert(progressData);
        
        if (error) throw error;
      }

      // Update local state
      setLessonProgress(prev => ({
        ...prev,
        [lessonId]: { ...progressData, id: existing?.id || Date.now() }
      }));

      if (completed) {
        setCompletedLessons(prev => 
          prev.includes(lessonId) ? prev : [...prev, lessonId]
        );
        
        // Check if this is the final quiz - generate certificate for any passing score (80%+)
        const isLastLesson = lessonId === totalLessons;
        const isFinalQuizPass = score >= 80 && isLastLesson;
        
        if (isFinalQuizPass) {
          await generateCertificate();
        }
        
        // Update enrollment progress percentage
        await updateEnrollmentProgress();
        
        toast.success('Progress saved successfully!');
      }
    } catch (error) {
      console.error('Error updating lesson progress:', error);
      toast.error('Failed to save progress');
    }
  };

  const generateCertificate = async () => {
    // Certificate generation is now handled server-side by the submit-quiz edge function
    // when the user passes the final quiz of a course. This client-side function is a no-op
    // kept for backward compatibility with existing call sites.
    return;
  };

  const updateEnrollmentProgress = async () => {
    if (!user || !userEnrollment) return;

    const completedCount = completedLessons.length + 1; // +1 for the just completed lesson
    const progressPercentage = Math.round((completedCount / totalLessons) * 100);

    try {
      const { error } = await supabase
        .from('user_enrollments')
        .update({ 
          progress_percentage: progressPercentage,
          completed_at: progressPercentage === 100 ? new Date().toISOString() : null
        })
        .eq('id', userEnrollment.id);

      if (error) throw error;

      // Update local enrollment state
      setUserEnrollment(prev => ({
        ...prev,
        progress_percentage: progressPercentage,
        completed_at: progressPercentage === 100 ? new Date().toISOString() : prev.completed_at
      }));
    } catch (error) {
      console.error('Error updating enrollment progress:', error);
    }
  };

  const handleAuthSuccess = () => {
    setShowProfileSetup(true);
  };

  const handleProfileComplete = () => {
    setShowProfileSetup(false);
    handleEnrollment();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 pt-24 pb-8 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading academy...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show lesson viewer if a lesson is selected
  if (currentLesson) {
    return (
      <LessonViewer
        lesson={currentLesson}
        onComplete={handleLessonComplete}
        onClose={() => setCurrentLesson(null)}
      />
    );
  }

  // Show badge viewer if requested
  if (showBadge) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 pt-24 pb-8 max-w-4xl">
          <div className="mb-6">
            <Button 
              variant="outline" 
              onClick={() => setShowBadge(false)}
              className="mb-4"
            >
              ← Back to Course
            </Button>
            <h1 className="text-3xl font-bold">Your L1 AI Badge</h1>
            <p className="text-muted-foreground">Digital credential for AI Fundamentals & Prompt Engineering</p>
          </div>
          <BadgeViewer 
            courseTitle={course.title}
            userProfile={null} // Will load from component
          />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "AI Fundamentals & Prompt Engineering",
          "description": "Master AI fundamentals and prompt engineering with our free 15-hour certified course. Learn from industry experts and earn a professional certificate.",
          "provider": {
            "@type": "Organization",
            "name": "QuantumBox AI",
            "url": "https://quantumbox.ai"
          },
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "category": "Free"
          },
          "educationalLevel": "Beginner",
          "timeRequired": "PT15H",
          "courseMode": "online",
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "instructor": {
              "@type": "Organization",
              "name": "QuantumBox AI Team"
            }
          },
          "educationalCredentialAwarded": "Certificate in AI Fundamentals & Prompt Engineering"
        })}
      </script>

      <main className="container mx-auto px-4 pt-24 pb-8 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge variant="default" className="mb-4 bg-green-100 text-green-800 border-green-200">
            🎓 FREE CERTIFICATION COURSE
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-purple-500 to-primary bg-clip-text text-transparent">
            AI Fundamentals & Prompt Engineering
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Master the fundamentals of Artificial Intelligence and learn advanced prompt engineering techniques. 
            <strong className="text-primary"> Get certified for FREE by QuantumBox AI.</strong>
          </p>
          
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              <span className="font-medium">{course.duration_hours} hours</span>
            </div>
            <div className="flex items-center gap-2">
              <Book className="h-5 w-5 text-primary" />
              <span className="font-medium">{totalLessons} lessons</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              <span className="font-medium">4.9/5 (15,420+ students)</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              <span className="font-medium">Industry Recognized</span>
            </div>
          </div>

          <Button 
            size="lg" 
            className="text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
            onClick={handleStartCourse}
          >
            {userEnrollment ? 'Continue Learning' : 'Start Free Course Now'}
            <Zap className="ml-2 h-5 w-5" />
          </Button>
        </div>

        {/* Course Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card className="text-center border-primary/20">
            <CardContent className="p-6">
              <Award className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Free Certification</h3>
              <p className="text-sm text-muted-foreground">
                Industry-recognized certificate from QuantumBox AI
              </p>
            </CardContent>
          </Card>
          
          <Card className="text-center border-primary/20">
            <CardContent className="p-6">
              <Shield className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Expert Instruction</h3>
              <p className="text-sm text-muted-foreground">
                Taught by QuantumBox AI professionals
              </p>
            </CardContent>
          </Card>
          
          <Card className="text-center border-primary/20">
            <CardContent className="p-6">
              <Users className="h-12 w-12 text-blue-500 mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Community Access</h3>
              <p className="text-sm text-muted-foreground">
                Join 15,000+ AI practitioners worldwide
              </p>
            </CardContent>
          </Card>
          
          <Card className="text-center border-primary/20">
            <CardContent className="p-6">
              <Clock className="h-12 w-12 text-purple-500 mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Lifetime Access</h3>
              <p className="text-sm text-muted-foreground">
                Learn at your pace, access forever
              </p>
            </CardContent>
          </Card>
        </div>

        {/* User Progress Section */}
        {user && userEnrollment && (completionPercentage > 0 || lessonProgress['21']?.completed) && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Your Progress</CardTitle>
                <Badge variant="secondary">{completionPercentage}% Complete</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Progress value={completionPercentage} className="mb-4" />
              <p className="text-sm text-muted-foreground">
                {completedLessons.length} of {totalLessons} lessons completed
              </p>
              
              {/* Show badge section if final exam is completed, regardless of overall progress */}
              {(completionPercentage === 100 || lessonProgress['21']?.completed) && (
                <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                      <Star className="h-5 w-5 text-white fill-current" />
                    </div>
                    <div>
                      <span className="font-bold text-blue-800">Level 1 Certified!</span>
                      <p className="text-sm text-blue-600">AI Fundamentals & Prompt Engineering</p>
                    </div>
                  </div>
                  <p className="text-blue-700 mb-4">
                    🎉 Congratulations! You've earned your L1 AI digital badge. Share your achievement with the world!
                  </p>
                  <Button 
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                    onClick={() => setShowBadge(true)}
                  >
                    <Award className="h-4 w-4 mr-2" />
                    View Your L1 Badge
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Course Curriculum */}
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Comprehensive Course Curriculum</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {totalLessons} lessons across {modules.length} modules designed to take you from beginner to advanced AI practitioner
            </p>
          </div>
          
          {modules.map((module, index) => (
            <Card key={module.id} className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold text-lg">
                    {index + 1}
                  </div>
                  <div>
                    <CardTitle className="text-xl">{module.title}</CardTitle>
                    <CardDescription className="text-base mt-1">
                      {module.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-0">
                  {module.lessons.map((lesson, lessonIndex) => {
                    const isCompleted = completedLessons.includes(lesson.id);
                    const isUnlocked = isLessonUnlocked(lesson.id);
                    return (
                      <div
                        key={lesson.id}
                        className={`flex items-center justify-between p-4 border-b last:border-b-0 transition-colors ${
                          isUnlocked ? 'hover:bg-muted/30 cursor-pointer' : 'opacity-60 cursor-not-allowed bg-gray-50/50 dark:bg-gray-900/20'
                        }`}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground w-6">
                              {lessonIndex + 1}.
                            </span>
                            {user ? (
                              <div className="flex items-center">
                                {isCompleted ? (
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                ) : isUnlocked ? (
                                  <Circle className="h-5 w-5 text-muted-foreground" />
                                ) : (
                                  <Circle className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                            ) : (
                              <div className="h-5 w-5 flex items-center justify-center">
                                {getLessonIcon(lesson.type)}
                              </div>
                            )}
                          </div>
                          
                          <button 
                            onClick={() => isUnlocked && handleLessonClick(lesson)}
                            className={`flex-1 text-left p-2 rounded transition-colors ${
                              isUnlocked ? 'hover:bg-muted/50' : 'cursor-not-allowed'
                            }`}
                            disabled={!isUnlocked}
                          >
                            <div className="flex items-center gap-3 mb-1">
                              <span className={`font-medium ${!isUnlocked ? 'text-gray-400' : ''}`}>
                                {lesson.title}
                                {!isUnlocked && ' 🔒'}
                              </span>
                              <Badge variant="outline" className={`${getBadgeColor(lesson.type)} ${!isUnlocked ? 'opacity-50' : ''}`}>
                                {lesson.type === 'final_exam' ? 'Final Exam' : 
                                 lesson.type === 'practical' ? 'Workshop' : lesson.type}
                              </Badge>
                            </div>
                            <p className={`text-sm ${!isUnlocked ? 'text-gray-400' : 'text-muted-foreground'}`}>
                              {lesson.description}
                            </p>
                          </button>
                        </div>
                        
                        <div className="text-right">
                          <span className={`text-sm font-medium ${!isUnlocked ? 'text-gray-400' : ''}`}>
                            {lesson.duration}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        {!user && (
          <Card className="mt-12 bg-gradient-to-r from-primary/5 to-purple-500/5 border-primary/20">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Start Your AI Journey?</h3>
              <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
                Join thousands of students who have already transformed their careers with our free AI certification course. 
                Start learning today and earn your certificate from QuantumBox AI.
              </p>
              <Button 
                size="lg" 
                className="text-lg px-8 py-6"
                onClick={handleStartCourse}
              >
                Get Started - It's Free!
                <Award className="ml-2 h-5 w-5" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* B2B CTA — Train Your Team */}
        <Card className="mt-12 border-primary/30 bg-gradient-to-br from-primary/10 via-background to-accent/10 overflow-hidden">
          <CardContent className="p-8 md:p-12 grid md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <Badge variant="outline" className="mb-3">For Teams & Enterprises</Badge>
              <h3 className="text-2xl md:text-3xl font-bold mb-3">
                Bring this training to your <span className="text-primary">entire team</span>
              </h3>
              <p className="text-muted-foreground">
                Custom cohorts, sector-specific tracks (Mining, Legal, Finance, Health), bilingual EN/FR delivery, admin dashboards, and verifiable certifications for your workforce.
              </p>
            </div>
            <div className="flex md:justify-end">
              <a href="/train-your-team">
                <Button size="lg" variant="hero">
                  Train Your Team
                  <Users className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Modals */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />

      {showProfileSetup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-background rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <ProfileSetup onComplete={handleProfileComplete} />
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default Academy;